import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import { hasPermission, PERMISSIONS } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';
import Phase7TemplateManagement from '../components/Phase7TemplateManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const stateOptions = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

export default function FunderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('funder-detail', '/funders');

  const [funder, setFunder] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // QUARANTINED: 24 Phase 6B state variables moved to FunderDetailPhase6Legacy.js

  const fetchFunderData = useCallback(async () => {
    try {
      setLoading(true);
      const funderRes = await axios.get(`${API_URL}/funding-entities/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFunder(funderRes.data);
      setEditData(funderRes.data);

      // Fetch deals related to this funder
      try {
        const dealsRes = await axios.get(`${API_URL}/deals`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const funderDeals = (dealsRes.data.deals || dealsRes.data).filter(d => d.funding_entity_id === parseInt(id));
        setDeals(funderDeals);
      } catch (e) {
        console.error('Failed to fetch deals:', e);
        setDeals([]);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load funder');
      console.error('Funder fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  // QUARANTINED: Phase 6B handler fetchTemplatesAndVersions and its useEffect moved to FunderDetailPhase6Legacy.js

  useEffect(() => {
    fetchFunderData();
  }, [fetchFunderData]);

  // Set up unified header
  useEffect(() => {
    if (!funder) return;

    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: funder.display_name || funder.legal_name,
      subtitle: null,
      actions: [
        hasPermission(PERMISSIONS.FUNDING_ENTITY_EDIT) && !editMode && (
          <button key="edit" onClick={() => setEditMode(true)} style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}>
            Edit
          </button>
        ),
      ].filter(Boolean),
    });

    return () => resetHeaderConfig();
  }, [funder, editMode, handleBack, setHeaderConfig, resetHeaderConfig]);

  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload = {
        legal_name: editData.legal_name,
        display_name: editData.display_name,
        abbreviation: editData.abbreviation,
        address: editData.address,
        city: editData.city,
        state: editData.state,
        zip: editData.zip,
        phone: editData.phone,
        email: editData.email,
      };

      await axios.put(`${API_URL}/funding-entities/${id}`, updatePayload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      setFunder(editData);
      setEditMode(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save funder');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // QUARANTINE: Phase 6B handlers extracted to FunderDetailPhase6Legacy.js

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
          <p style={TYPOGRAPHY.BODY}>Loading funder...</p>
        </div>
      </div>
    );
  }

  if (!funder) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL }}>
          <h3 style={TYPOGRAPHY.H3}>Funder not found</h3>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        {error && (
          <div style={{
            padding: SPACING.LG,
            backgroundColor: COLORS.STATUS_DECLINED_BG,
            border: `1px solid ${COLORS.ERROR}`,
            borderRadius: RADIUS.DEFAULT,
            color: COLORS.ERROR,
            marginBottom: SPACING.XL,
          }}>
            {error}
          </div>
        )}

        {/* Funder Information Section */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Funder Information</h2>
            {editMode && (
              <div style={{ display: 'flex', gap: SPACING.MD }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    ...styles.saveButton,
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditData(funder);
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {editMode ? (
            <div style={styles.editForm}>
              <div style={styles.gridTwo}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Legal Name *</label>
                  <input
                    type="text"
                    value={editData.legal_name || ''}
                    onChange={(e) => handleEditChange('legal_name', e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>DBA / Display Name</label>
                  <input
                    type="text"
                    value={editData.display_name || ''}
                    onChange={(e) => handleEditChange('display_name', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.gridTwo}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Abbreviation</label>
                  <input
                    type="text"
                    value={editData.abbreviation || ''}
                    onChange={(e) => handleEditChange('abbreviation', e.target.value)}
                    style={styles.input}
                    maxLength="5"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={editData.phone || ''}
                  onChange={(e) => handleEditChange('phone', e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.gridTwo}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Address</label>
                  <input
                    type="text"
                    value={editData.address || ''}
                    onChange={(e) => handleEditChange('address', e.target.value)}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    value={editData.city || ''}
                    onChange={(e) => handleEditChange('city', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.gridTwo}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>State</label>
                  <select
                    value={editData.state || ''}
                    onChange={(e) => handleEditChange('state', e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Select state</option>
                    {stateOptions.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>ZIP Code</label>
                  <input
                    type="text"
                    value={editData.zip || ''}
                    onChange={(e) => handleEditChange('zip', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Legal Name</span>
                <span style={styles.infoValue}>{funder.legal_name || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Display Name</span>
                <span style={styles.infoValue}>{funder.display_name || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Abbreviation</span>
                <span style={styles.infoValue}>{funder.abbreviation || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Email</span>
                <span style={styles.infoValue}>{funder.email || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Phone</span>
                <span style={styles.infoValue}>{funder.phone || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Address</span>
                <span style={styles.infoValue}>{funder.address || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>City</span>
                <span style={styles.infoValue}>{funder.city || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>State</span>
                <span style={styles.infoValue}>{funder.state || '—'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>ZIP Code</span>
                <span style={styles.infoValue}>{funder.zip || '—'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Phase 7: PDF-Native Contract Templates */}
        <Phase7TemplateManagement fundingEntityId={parseInt(id)} token={token} />

        {/* Related Deals / Advances Section */}
        {deals.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Related Advances</h2>
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.headerRow}>
                    <th style={styles.headerCell}>Advance ID</th>
                    <th style={styles.headerCell}>Merchant</th>
                    <th style={styles.headerCell}>Status</th>
                    <th style={styles.headerCell}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal, idx) => (
                    <tr
                      key={deal.id}
                      style={{
                        ...styles.tableRow,
                        borderBottom: idx < deals.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.BG_SURFACE_SECONDARY}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{...styles.tableCell, fontWeight: '600', cursor: 'pointer'}} onClick={() => navigate(`/deals/${deal.id}`)}>
                        {deal.visual_deal_id || `Deal ${deal.id}`}
                      </td>
                      <td style={styles.tableCell}>
                        {deal.merchant?.legal_name || deal.merchant_id || '—'}
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{
                          ...styles.badge,
                          backgroundColor: deal.status === 'funded' ? COLORS.STATUS_FUNDED_BG : COLORS.STATUS_ACTIVE_BG,
                          color: deal.status === 'funded' ? COLORS.STATUS_FUNDED_TEXT : '#92400e', // Warm brown for pending/active deals
                        }}>
                          {deal.status || '—'}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        {deal.working_data?.requested_amount || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {deals.length === 0 && (
          <div style={styles.card}>
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>💼</div>
              <h3 style={styles.emptyTitle}>No advances yet</h3>
              <p style={styles.emptyText}>
                No advances have been associated with this funder.
              </p>
            </div>
          </div>
        )}

        {/* QUARANTINE: Phase 6B modals (Create Template, Create Version, Add Field, Delete Field) extracted to FunderDetailPhase6Legacy.js */}
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
    paddingTop: SPACING.XL,
    paddingBottom: SPACING.XXXL,
  },

  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: `0 ${SPACING.XL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.XL,
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    boxShadow: SHADOWS.DEFAULT,
    overflow: 'hidden',
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  cardTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACING.LG,
    padding: SPACING.LG,
  },

  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.XS,
  },

  infoLabel: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },

  infoValue: {
    ...TYPOGRAPHY.BODY,
    color: COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },

  editForm: {
    padding: SPACING.LG,
  },

  formGroup: {
    marginBottom: SPACING.LG,
  },

  label: {
    display: 'block',
    ...TYPOGRAPHY.BODY,
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    marginBottom: SPACING.SM,
  },

  input: {
    width: '100%',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
  },

  select: {
    width: '100%',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
  },

  gridTwo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACING.LG,
  },

  emptyState: {
    padding: SPACING.XXXL,
    textAlign: 'center',
  },

  emptyIcon: {
    fontSize: '64px',
    marginBottom: SPACING.XL,
  },

  emptyTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: `0 0 ${SPACING.MD} 0`,
  },

  emptyText: {
    ...TYPOGRAPHY.BODY,
    color: COLORS.TEXT_LIGHT,
    margin: `0 0 ${SPACING.MD} 0`,
  },

  emptySubtext: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
    margin: 0,
    fontStyle: 'italic',
  },

  tableWrapper: {
    overflow: 'auto',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  headerRow: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  headerCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    textAlign: 'left',
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  tableRow: {
    transition: 'background-color 0.2s ease',
  },

  tableCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    color: COLORS.TEXT_PRIMARY,
  },

  badge: {
    padding: `${SPACING.XS} ${SPACING.SM}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
  },

  saveButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  cancelButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  modalContent: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    boxShadow: SHADOWS.DEFAULT,
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
  },

  modalHeader: {
    padding: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  modalTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  modalBody: {
    padding: SPACING.LG,
  },

  modalFooter: {
    padding: SPACING.LG,
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
    display: 'flex',
    gap: SPACING.MD,
    justifyContent: 'flex-end',
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },
};
